import { FileType, FolderItem, FOLDER_COLORS, LibraryFile, Visibility } from 'mock/folder.data';
import { Document, Folder, LibraryContentAPIResponse } from './type';

const DEFAULT_COLOR_ID = '3';
const FALLBACK_DATE = '1970-01-01T00:00:00.000Z';

function mapVisibility(isPublic?: boolean): Visibility {
	return isPublic ? 'public' : 'private';
}

function mapColorToColorId(color?: string): string {
	if (!color) {
		return DEFAULT_COLOR_ID;
	}

	if (FOLDER_COLORS[color]) {
		return color;
	}

	const normalizedColor = color.toLowerCase();
	const matchedEntry = Object.entries(FOLDER_COLORS).find(([, palette]) => (
		palette.startColor.toLowerCase() === normalizedColor || palette.endColor.toLowerCase() === normalizedColor
	));

	return matchedEntry?.[0] ?? DEFAULT_COLOR_ID;
}

function isSystemOwned(rolePartition?: string): boolean {
	if (!rolePartition) {
		return false;
	}

	const normalizedRole = rolePartition.toLowerCase();
	return normalizedRole.includes('admin') || normalizedRole.includes('system');
}

function mapOwner(rolePartition?: string, createdBy?: number): string {
	if (isSystemOwned(rolePartition)) {
		return 'Hệ thống';
	}

	if (typeof createdBy === 'number') {
		return 'Tôi';
	}

	return 'Tôi';
}

function inferFileType(document: Document): FileType {
	const source = [document.extension, document.mime_type, document.name].filter(Boolean).join(' ').toLowerCase();

	if (/(png|jpg|jpeg|gif|webp|svg|image)/.test(source)) {
		return 'IMAGE';
	}

	if (/(mp4|mov|avi|mkv|video)/.test(source)) {
		return 'VIDEO';
	}

	if (/(ppt|pptx|powerpoint)/.test(source)) {
		return 'PPTX';
	}

	if (/(xls|xlsx|csv|spreadsheet)/.test(source)) {
		return 'XLSX';
	}

	if (/(doc|docx|word)/.test(source)) {
		return 'DOCX';
	}

	return 'PDF';
}

function mapFolderToFolderItem(folder: Folder): FolderItem {
	return {
		id: String(folder.id),
		name: folder.name,
		colorId: mapColorToColorId(folder.color),
		parentId: folder.parent_id !== null ? String(folder.parent_id) : null,
		owner: mapOwner(folder.role_partition, folder.created_by),
		createdBy: folder.created_by,
		createdAt: folder.created_at || FALLBACK_DATE,
		updatedAt: folder.updated_at || folder.created_at || FALLBACK_DATE,
		visible: mapVisibility(folder.is_public),
		isAdminUpload: isSystemOwned(folder.role_partition),
	};
}

function mapDocumentToLibraryFile(document: Document, currentParentId: number | null): LibraryFile {
	const folderId = document.parent_id ?? currentParentId;

	return {
		id: String(document.id),
		name: document.name,
		type: inferFileType(document),
		folderId: folderId !== null ? String(folderId) : null,
		size: document.size ?? 0,
		owner: mapOwner(document.role_partition, document.uploaded_by ?? document.created_by),
		createdBy: document.uploaded_by ?? document.created_by,
		createdAt: document.created_at || FALLBACK_DATE,
		updatedAt: document.updated_at || document.created_at || FALLBACK_DATE,
		visible: mapVisibility(document.is_public),
		isAdminUpload: isSystemOwned(document.role_partition),
	};
}

function mapLibraryContentResponse(response: LibraryContentAPIResponse): { folders: FolderItem[]; files: LibraryFile[] } {
	const currentParentId = response.data.current_parent_id;

	return {
		folders: response.data.folders.map(mapFolderToFolderItem),
		files: response.data.documents.map((document) => mapDocumentToLibraryFile(document, currentParentId)),
	};
}

function mapConfirmedUploadToLibraryFile(
	data: { id: number; name: string; role_partition: string; created_at: string; updated_at: string; is_system: boolean },
	originalFile: File,
	folderId: string | null
): LibraryFile {
	return {
		id: String(data.id),
		name: data.name || originalFile.name,
		type: inferFileType({ id: data.id, name: originalFile.name, mime_type: originalFile.type } as Document),
		folderId,
		size: originalFile.size,
		owner: 'Tôi',
		createdAt: data.created_at || new Date().toISOString(),
		updatedAt: data.updated_at || data.created_at || new Date().toISOString(),
		visible: data.is_system ? 'public' : 'private',
		isAdminUpload: data.is_system,
	};
}

export { mapFolderToFolderItem, mapLibraryContentResponse, mapConfirmedUploadToLibraryFile };
